import { ActionContext, Module } from "vuex";

import {
  // API_BIS_DATA_SYNC_PROGRESS,
  API_BIS_DATA_SYNC_TASKS,
  API_BIS_IMPORT_PROGRESS,
  API_BIS_PATENTTEXT_PROGRESS,
} from "@/configs/Apis";
import { isEmptyOrWhiteSpace } from "@/utils/Common";
import DuduAjax from "@/utils/DuduAjax";

declare interface BaseTaskStatus {
  taskId: string;
  projectId: string;
  status: string;
  count: number;
  finished: number;
  taskToken: string;
}

declare interface ImportTaskStatus extends BaseTaskStatus {
  success: number;
  finished: number;
  failed: number;
  skipped: number;
  messages: Nullable<Array<string>>;
}

declare interface DataSyncTaskStatus extends Omit<BaseTaskStatus, "count" | "finished"> {
  /** 检索任务唯一标识 */
  requestUuid: string;
  /** WIPS 命中总数（取已回推日志中的最新值） */
  totalCount: number;
  /** 累计实际入库条数 */
  insertCount: number;
  /** 累计重复跳过条数 */
  skipCount: number;
  /** 累计入库异常条数 */
  failCount: number;
  /** 已回推日志页数 */
  pageCount: number;
  /** 是否已收到最后一页（任务收尾） */
  finished: boolean;
}

const defaultImportTask = {
  status: "NOT_STARTED",
  count: 0,
  finished: 0,
  success: 0,
  failed: 0,
  skipped: 0,
  messages: null,
};

const defaultPatentTextStatus = {
  status: "NOT_STARTED",
  count: 0,
  finished: 0,
};

const defaultDataSyncStatus: DataSyncTaskStatus = {
  taskId: "",
  projectId: "",
  taskToken: "",
  status: "NOT_STARTED",
  requestUuid: "",
  totalCount: 0,
  insertCount: 0,
  skipCount: 0,
  failCount: 0,
  pageCount: 0,
  finished: false,
};

declare interface TaskState {
  importTask: ImportTaskStatus;
  patentTextTask: BaseTaskStatus;
  dataSyncTask: DataSyncTaskStatus;
}

const module: Module<TaskState, any> = {
  namespaced: true,
  state: {
    importTask: Object.assign({ taskId: "", projectId: "", taskToken: "" }, defaultImportTask),
    patentTextTask: Object.assign({ taskId: "", projectId: "", taskToken: "" }, defaultPatentTextStatus),
    dataSyncTask: Object.assign({ taskId: "", projectId: "", taskToken: "" }, defaultDataSyncStatus),
  },
  mutations: {
    importTask(state, payload) {
      state.importTask = payload;
    },
    patentTextTask(state, payload) {
      state.patentTextTask = payload;
    },
    dataSyncTask(state, payload) {
      state.dataSyncTask = payload;
    },
  },

  getters: {
    isProgress(state) {
      return (
        state.importTask.status === "RUNNING" ||
        state.patentTextTask.status === "RUNNING" ||
        state.dataSyncTask.status === "RUNNING"
      );
    },
  },

  actions: {
    progressImport(store, payload: { taskId: string; projectId: string }) {
      return requestProgress(store, API_BIS_IMPORT_PROGRESS, "importTask", payload, defaultImportTask);
    },
    progressPatentText(store, payload: { taskId: string; projectId: string }) {
      return requestProgress(store, API_BIS_PATENTTEXT_PROGRESS, "patentTextTask", payload, defaultPatentTextStatus);
    },
    progressDataSync(store, payload: { projectId: string; taskId: string }) {
      const { projectId, taskId } = payload;
      // 立即置为运行中，让右下角"有同步任务正在运行"提示无需刷新即可显示
      store.commit(
        "dataSyncTask",
        Object.assign({ taskId, projectId, requestUuid: taskId }, defaultDataSyncStatus, { status: "RUNNING" })
      );
      // 后台轮询运行中的任务，刷新"共 N 个任务正在运行"的实时数量
      const poll = () => {
        DuduAjax.get(API_BIS_DATA_SYNC_TASKS, { projectId, status: "RUNNING", page: 0, pageSize: 100 }, false)
          .then((res: any) => {
            if (res && res.data && res.data.length > 0) {
              store.commit("dataSyncTask", { status: "RUNNING", totalCount: res.count });
              setTimeout(poll, 2000);
            } else {
              // 运行中的任务已全部完成，恢复非运行态，自动隐藏右下角提示
              store.commit("dataSyncTask", Object.assign({}, defaultDataSyncStatus));
            }
          })
          .catch(() => {});
      };
      poll();
      return Promise.resolve();
    },
    async getDataSyncList(store, payload) {
      const res: any = await DuduAjax.get(API_BIS_DATA_SYNC_TASKS, { projectId: 0, pageSize: 100, status: "RUNNING" });
      if (res && res.data.length > 0) {
        store.commit("dataSyncTask", { status: "RUNNING", totalCount: res.count });
      }
    },
  },
};

function requestProgress(
  store: ActionContext<TaskState, any>,
  api: string,
  path: string,
  payload: { taskId: string; projectId: string },
  defaultValue: any
) {
  return new Promise<void>((resolve, reject) => {
    let taskToken: any = "";

    if (!isEmptyOrWhiteSpace((store.state as any)[path].taskToken)) {
      clearTimeout((store.state as any)[path].taskToken as any);
    }

    store.commit(
      path,
      Object.assign({ taskId: payload.taskId, projectId: payload.projectId, taskToken }, defaultValue)
    );

    if (isEmptyOrWhiteSpace(payload.taskId) || isEmptyOrWhiteSpace(payload.taskId)) {
      return reject(new Error("参数错误"));
    }

    function progress() {
      DuduAjax.get<BaseTaskStatus>(api, { projectId: payload.projectId, taskId: payload.taskId }, false)
        .then((message) => {
          if (message.status === "COMPLETED") {
            clearTimeout(taskToken);
            resolve();
          } else {
            taskToken = setTimeout(progress, 2000);
          }
          store.commit(
            path,
            Object.assign({ taskId: payload.taskId, projectId: payload.projectId, taskToken }, defaultValue, message)
          );
        })
        .catch(() => "");
    }

    progress();
  });
}

export default module;
